import {
    HttpClient,
    HttpContext,
    HttpHeaders,
    HttpErrorResponse,
    HttpResponse
} from '@angular/common/http';
import { SKIP_AUTH_UNAUTHORIZED_RELOAD } from '@app/core/auth/auth-http.context';
import { Injectable, inject, Injector } from '@angular/core';
import { CredentialsCurrentUser, CredentialsService } from '@app/core';
import {
    ClientDiscountPercents,
    ClientFeePercents,
    PremiumOrder,
    Producto,
    Rfq,
    Category,
    User,
    Client,
    UsersBrands
} from '@app/core/services/api/API.service';
import { environment } from '@env/environment';
import { fetchAuthSession } from 'aws-amplify/auth';
import { firstValueFrom, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { File as CustomFile } from '@app/core/services/api/API.service';
import { AuthService } from '@app/core/auth/auth.service';
import { ClientsService } from '@app/shared/services/preloaded/auth/clients.service';
import { UsersBrandsPreloadService } from '@app/shared/services/preloaded/auth/users-brands.service';
import { ROLES } from '@app/constants';
// Generated by https://quicktype.io

export interface ClientApi extends Client {
    usersBrandsLength?: number;
}

export interface ListPremiumOrdersOptions {
    sortBy?: 'orderId' | 'createdAt' | 'updatedAt';
    sortOrder?: 'asc' | 'desc';
}

export interface PremiumOrderListItem {
    currentProcess: string;
    action: number;
    step: number;
    processes: number;
    currentSubprocess: string;
    id: string;
    orderId: number;
    requirement: string;
    createdAt: string;
    orderCreatorId?: string;
    orderCreatorName: string;
    orderCreatorEmail: string;
    clientBrandId: string;
    brandId?: string;
    clientId: string;
    clientName: string;
    clientLogo?: string;
    userBrand?: UsersBrands;
    syncWithCrm?: boolean;
    sentToAlendum?: boolean;
    syncWithFms?: string | null;
    fmsInvoiceId?: string | null;
}

export interface PopMaterialAdminItem {
    clientDiscounts: ClientDiscountPercents[];
    image: CustomFile;
    updatedAt: string;
    route: string;
    year?: string;
    createdAt: string;
    visibleFor?: string[];
    id: string;
    name: string;
    clientFeeIncrements: ClientFeePercents[];
    clientIncrements: ClientFeePercents[];
    popCategoriesCount: number;
    vehicleTypesCount: number;
    vehiclesCount: number;
}

export interface StatePremiumRfqs {
    status: string;
    client: string;
}

export interface BestSellersResponse {
    products: Producto[];
    categories: Category[];
}

export interface BestSellerProductsOptions {
    clientId?: string;
    showPublicPrices?: boolean;
    providerId?: string;
    nameExternal?: string;
    limit?: number;
    useAuth?: boolean;
}

export interface PremiumProcessAdminItem {
    clientId: string;
    clientName: string;
    status: boolean;
    id: string;
    image: CustomFile;
    name: string;
    position: number;
    subprocessesLength: number;
}

/** Tokens Cognito devueltos por GET `ping-id` (intercambio Ping ID → user pool). */
export interface PingIdAuthTokens {
    AccessToken: string;
    IdToken: string;
    RefreshToken: string;
    email: string;
    id: string;
}

export interface CategoryAPI extends Category {
    productsCount: number;
    masksCount: number;
}

export interface CategoryNodeResponse {
    data: CategoryNode[];
    index: CategoryNode;
}

export interface CategoryNode {
    id: string;
    name: string;
    path: string;
    children: CategoryNode[];
    image?: Image;
}

export interface ClientIncrement {
    index: number;
    percent: number;
    clientId: string;
}

export interface Image {
    key: string;
}

@Injectable({
    providedIn: 'root'
})
export class BrandfactoryServerlessService {
    private currentUser: CredentialsCurrentUser;
    private _authService = inject(AuthService);
    private _injector = inject(Injector);
    private _premiumCategoriesInFlight = new Map<
        string,
        Promise<CategoryAPI[] | CategoryNode[] | CategoryNodeResponse>
    >();

    constructor(
        private http: HttpClient,
        private _credentialsService: CredentialsService
    ) {}

    /**
     * GET product-detail/{idProduct}.
     * showPublicPrices: solo para usuarios no autenticados; "true" para que el API devuelva precios. No aplica si useAuth=true.
     * clientId: aplica máscaras del cliente (mismo criterio que best-seller-products / list-premium-categories).
     */
    async getProductsDetailById(
        id: string,
        useAuth: boolean = true,
        options?: { clientId?: string }
    ): Promise<Producto> {
        try {
            let headers: HttpHeaders;
            if (useAuth) {
                headers = await this._authService.generateHeaders();
            } else {
                headers = new HttpHeaders({
                    'Content-Type': 'application/json'
                });
            }

            const params: Record<string, string> = {};
            if (!useAuth) {
                params.showPublicPrices = environment.showPublicPrices
                    ? 'true'
                    : 'false';
            }
            if (options?.clientId) {
                params.clientId = options.clientId;
            }

            const response = await firstValueFrom(
                this.http.get<any>(
                    `${environment.api2Url}product-detail/${id}`,
                    {
                        observe: 'response',
                        headers,
                        params:
                            Object.keys(params).length > 0 ? params : undefined
                    }
                )
            );
            if (response) {
                return response.body.data;
            }
        } catch (err) {
            const status = err?.status || err?.statusCode || 500;
            const errorBody = err?.error || {};
            const errorMessage =
                errorBody?.message ||
                err?.message ||
                errorBody?.error ||
                'Error desconocido';

            const errorInfo = {
                status: status,
                message:
                    typeof errorMessage === 'string'
                        ? errorMessage
                        : JSON.stringify(errorMessage),
                error: errorBody || err,
                statusCode: status
            };
            throw errorInfo;
        }
    }

    async listClients(): Promise<ClientApi[]> {
        try {
            const headers = await this._authService.generateHeaders();
            const response = await firstValueFrom(
                this.http.get<ClientApi[]>(
                    `${environment.api2Url}list-clients`,
                    {
                        observe: 'response',
                        headers
                    }
                )
            );
            if (response) {
                return response.body;
            }
        } catch (err) {
            console.error(err);
        }
    }

    async listBrands(): Promise<UsersBrands[]> {
        try {
            const headers = await this._authService.generateHeaders();

            const response = await firstValueFrom(
                this.http
                    .get<UsersBrands[]>(`${environment.api2Url}list-brands`, {
                        observe: 'response',
                        headers
                    })
                    .pipe(
                        catchError((error: HttpErrorResponse) => {
                            if (error.status === 500) {
                                return of(
                                    new HttpResponse<UsersBrands[]>({
                                        body: []
                                    })
                                );
                            }
                            return of(
                                new HttpResponse<UsersBrands[]>({ body: [] })
                            );
                        })
                    )
            );

            return response.body ?? [];
        } catch (error) {
            return [];
        }
    }

    async getPremiumOrder(id: string): Promise<PremiumOrder> {
        try {
            // Use the shared function to generate headers
            const token = (
                await fetchAuthSession()
            ).tokens?.idToken?.toString();
            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                Authorization: token
            });

            const response = await firstValueFrom(
                this.http.get<any>(
                    `${environment.api2Url}get-premium-order/${id}`,
                    {
                        observe: 'response',
                        headers
                    }
                )
            );
            if (response) {
                return response.body.data;
            }
        } catch (err) {
            console.error(err);
        }
    }

    async getCoupaToken(session: string, token: string): Promise<any> {
        try {
            const body = {
                session
            };
            const httpOptions = {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    Authorization: token
                })
            };
            const response = await firstValueFrom(
                this.http.post(`${environment.api2Url}coupa`, body, httpOptions)
            );
            if (response) {
                return response;
            }
        } catch (err) {
            console.error(err);
        }
    }

    private async resolveClients(): Promise<Client[]> {
        const clientsService = this._injector.get(ClientsService);
        return firstValueFrom(clientsService.clients$.pipe(take(1)));
    }

    private async resolveUsersBrands(): Promise<UsersBrands[]> {
        const usersBrandsService = this._injector.get(
            UsersBrandsPreloadService
        );
        await usersBrandsService.listUsersBrands();
        return firstValueFrom(usersBrandsService.usersBrands$.pipe(take(1)));
    }

    async listPremiumOrders(
        options: ListPremiumOrdersOptions = {}
    ): Promise<PremiumOrderListItem[]> {
        try {
            const headers = await this._authService.generateHeaders();
            const [clients, usersBrands] = await Promise.all([
                this.resolveClients(),
                this.resolveUsersBrands()
            ]);

            let url = `${environment.api2Url}list-premium-orders`;
            const query: string[] = [];
            if (options.sortBy) {
                query.push(`sortBy=${encodeURIComponent(options.sortBy)}`);
            }
            if (options.sortOrder) {
                query.push(
                    `sortOrder=${encodeURIComponent(options.sortOrder)}`
                );
            }
            if (query.length) {
                url += `?${query.join('&')}`;
            }

            const response = await firstValueFrom(
                this.http.get<{
                    data: {
                        orders: Array<{
                            action: number;
                            clientBrandId: string;
                            brandId?: string;
                            createdAt: string;
                            currentProcess: string;
                            currentSubprocess: string;
                            id: string;
                            orderCreatorEmail: string;
                            orderId: number;
                            processes: number;
                            requirement: string;
                            step: number;
                            syncWithCrm?: boolean;
                            sentToAlendum?: boolean;
                            syncWithFms?: string | null;
                            fmsInvoiceId?: string | null;
                        }>;
                        users: Array<{
                            orderCreatorId: string;
                            orderCreatorName: string;
                            email: string;
                        }>;
                    };
                }>(url, {
                    observe: 'response',
                    headers
                })
            );

            if (!response?.body?.data?.orders) {
                return [];
            }

            const { orders, users } = response.body.data;

            return orders.map(order => {
                const user = users.find(
                    u => u.email === order.orderCreatorEmail
                );
                const client = clients.find(c => c.id === order.clientBrandId);
                const userBrand = order.brandId
                    ? usersBrands.find(
                          usersBrand =>
                              usersBrand.id === order.brandId &&
                              usersBrand.clientId === order.clientBrandId
                      )
                    : undefined;

                return {
                    ...order,
                    orderCreatorId: user?.orderCreatorId,
                    orderCreatorName: user?.orderCreatorName ?? '',
                    orderCreatorEmail: order.orderCreatorEmail,
                    clientId: order.clientBrandId,
                    clientName:
                        userBrand?.name ??
                        client?.comertial_name ??
                        client?.business_name ??
                        '',
                    clientLogo: userBrand?.logo?.key ?? client?.logo?.key ?? '',
                    userBrand
                };
            });
        } catch (err) {
            console.error(err);
            return [];
        }
    }

    async listPremiumRfqs(bodyState: StatePremiumRfqs): Promise<Rfq[]> {
        try {
            this.currentUser = this._credentialsService.credentialsCurrentUser;
            let client: Client = null;
            if (this.currentUser.role === ROLES.COMPRAS) {
                /*client = await this._clientsService.findClientById(
                    this.currentUser.clientId
                );*/
            }

            // Use the shared function to generate headers
            const token = (
                await fetchAuthSession()
            ).tokens?.idToken?.toString();
            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                Authorization: token
            });

            const body: any = {
                estatus: bodyState.status,
                role: this.currentUser.role,
                clientId: this.currentUser.clientId
                    ? this.currentUser?.clientId
                    : bodyState.client === 'all'
                    ? null
                    : bodyState.client,
                userId: this.currentUser.id,
                allPremium:
                    this.currentUser?.role === ROLES.COMPRAS
                        ? this.currentUser?.company?.allPremium
                        : true,
                from: '2019-01-01',
                to: '2027-12-31'
            };
            if (this.currentUser.isCoupa) {
                body.role = ROLES.COMPRAS_SR;
            }
            const response = await firstValueFrom(
                this.http.post<any>(
                    `${environment.api2Url}list-premium-rfqs`,
                    body,
                    {
                        observe: 'response',
                        headers
                    }
                )
            );
            if (response) {
                return response.body.rfqs;
            }
        } catch (err) {
            console.error(err);
        }
    }

    async listPopMaterials(): Promise<PopMaterialAdminItem[]> {
        try {
            // Use the shared function to generate headers
            const token = (
                await fetchAuthSession()
            ).tokens?.idToken?.toString();
            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                Authorization: token
            });

            const response = await firstValueFrom(
                this.http.get<any>(`${environment.api2Url}list-pop-materials`, {
                    observe: 'response',
                    headers
                })
            );
            if (response) {
                return response.body.data;
            }
        } catch (err) {
            console.error(err);
            return [];
        }
    }

    /**
     * GET best-seller-products.
     * Soporta firma legacy: bestSellerProducts(clientId?, useAuth?) u opciones: bestSellerProducts({ clientId, showPublicPrices, ... }).
     * showPublicPrices: solo para usuarios no autenticados; "true" para que el API devuelva precios, "false" u omitir para ocultarlos.
     */
    async bestSellerProducts(
        clientIdOrOptions?: string | null | BestSellerProductsOptions,
        useAuth: boolean = true
    ): Promise<BestSellersResponse> {
        try {
            const options: BestSellerProductsOptions =
                clientIdOrOptions != null &&
                typeof clientIdOrOptions === 'object'
                    ? clientIdOrOptions
                    : {
                          clientId:
                              typeof clientIdOrOptions === 'string'
                                  ? clientIdOrOptions
                                  : undefined,
                          useAuth
                      };

            const useAuthHeaders = options.useAuth ?? true;
            let headers: HttpHeaders;
            if (useAuthHeaders) {
                headers = await this._authService.generateHeaders();
            } else {
                headers = new HttpHeaders({
                    'Content-Type': 'application/json'
                });
            }

            const params: Record<string, string> = {};
            if (options.clientId) {
                params.clientId = options.clientId;
            }
            if (options.providerId) {
                params.providerId = options.providerId;
            }
            if (options.nameExternal) {
                params.nameExternal = options.nameExternal;
            }
            if (options.limit != null && options.limit > 0) {
                params.limit = String(
                    Math.min(5000, Math.max(1, options.limit))
                );
            }
            if (!useAuthHeaders) {
                params.showPublicPrices =
                    options.showPublicPrices !== undefined
                        ? options.showPublicPrices
                            ? 'true'
                            : 'false'
                        : environment.showPublicPrices
                        ? 'true'
                        : 'false';
            }

            const response = await firstValueFrom(
                this.http.get<BestSellersResponse>(
                    `${environment.api2Url}best-seller-products`,
                    {
                        observe: 'response',
                        headers,
                        params
                    }
                )
            );
            if (response && response.body) {
                return response.body;
            }
            return null;
        } catch (err) {
            console.error(err);
            return null;
        }
    }

    async listUsers(currentUser: CredentialsCurrentUser): Promise<User[]> {
        try {
            const token = (
                await fetchAuthSession()
            ).tokens?.idToken?.toString();
            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                Authorization: token
            });
            const body: any = {
                role: currentUser.role,
                clientId: currentUser.clientId ? currentUser.clientId : null
            };

            const response = await firstValueFrom(
                this.http.post<any>(`${environment.api2Url}list-users`, body, {
                    observe: 'response',
                    headers
                })
            );
            if (response) {
                return response.body.data;
            }
        } catch (err) {
            console.error(err);
        }
    }

    async listPremiumCategories(): Promise<CategoryAPI[]>;
    async listPremiumCategories(parentId: string): Promise<CategoryAPI[]>;
    async listPremiumCategories(options: {
        parentId?: string;
        cascade?: false;
        clientId?: string;
        useAuth?: boolean;
    }): Promise<CategoryAPI[]>;
    async listPremiumCategories(options: {
        parentId?: string;
        cascade: true;
        clientId?: string;
        useAuth?: boolean;
    }): Promise<CategoryNode[]>;
    async listPremiumCategories(
        arg?:
            | string
            | {
                  parentId?: string;
                  cascade?: boolean;
                  clientId?: string;
                  useAuth?: boolean;
              }
    ): Promise<CategoryAPI[] | CategoryNode[] | CategoryNodeResponse> {
        try {
            let parentId: string | undefined;
            let cascade: boolean | undefined;
            let clientId: string | undefined;
            let useAuth = true;

            if (typeof arg === 'string') {
                parentId = arg;
            } else if (typeof arg === 'object' && arg !== null) {
                parentId = arg.parentId;
                cascade = arg.cascade;
                clientId = arg.clientId;
                if (arg.useAuth === false) {
                    useAuth = false;
                }
            }

            const headers = useAuth
                ? await this._authService.generateHeaders()
                : new HttpHeaders({
                      'Content-Type': 'application/json'
                  });

            let url = `${environment.api2Url}list-premium-categories`;
            const query: string[] = [];
            if (parentId) {
                query.push(`parentId=${encodeURIComponent(parentId)}`);
            }
            if (cascade) {
                query.push('cascade=true');
            }
            if (clientId) {
                query.push(`clientId=${encodeURIComponent(clientId)}`);
            }
            if (query.length) {
                url += `?${query.join('&')}`;
            }

            const requestKey = `${url}|auth:${useAuth}`;
            const inFlight = this._premiumCategoriesInFlight.get(requestKey);
            if (inFlight) {
                return inFlight;
            }

            const requestPromise = firstValueFrom(
                this.http.get<any>(url, {
                    observe: 'response',
                    headers
                })
            )
                .then(response => {
                    if (!response || !response.body) {
                        return [];
                    }

                    if (response.body.index) {
                        return response.body;
                    }

                    if (Array.isArray(response.body.data)) {
                        return response.body.data;
                    }

                    if (Array.isArray(response.body)) {
                        return response.body;
                    }

                    return [];
                })
                .catch(err => {
                    console.error('Error fetching premium categories:', err);
                    return [];
                })
                .finally(() => {
                    this._premiumCategoriesInFlight.delete(requestKey);
                });

            this._premiumCategoriesInFlight.set(requestKey, requestPromise);
            return requestPromise;
        } catch (err) {
            console.error('Error fetching premium categories:', err);
            return [];
        }
    }

    async listPremiumProcesses(): Promise<PremiumProcessAdminItem[]> {
        try {
            const token = (
                await fetchAuthSession()
            ).tokens?.idToken?.toString();
            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                Authorization: token
            });
            const response = await firstValueFrom(
                this.http.get<any>(
                    `${environment.api2Url}list-premium-process`,
                    {
                        observe: 'response',
                        headers
                    }
                )
            );
            if (response) {
                return response.body.data || [];
            }
        } catch (err) {
            console.error(err);
        }
    }

    async pingId(token: string): Promise<PingIdAuthTokens | undefined> {
        try {
            const headers = new HttpHeaders({
                Authorization: `Bearer ${token}`
            });
            const response = await firstValueFrom(
                this.http.get<PingIdAuthTokens>(
                    `${environment.api2Url}ping-id`,
                    {
                        headers,
                        observe: 'response',
                        context: new HttpContext().set(
                            SKIP_AUTH_UNAUTHORIZED_RELOAD,
                            true
                        )
                    }
                )
            );
            return response?.body ?? undefined;
        } catch (err) {
            console.error(err);
        }
    }
}
